import { useAnalytics } from "@codat/analytics";
import { Modal, useToast } from "@codat/orchard-ui";
import { useContactSalesMutation } from "api/public";
import React, { ChangeEventHandler, FormEventHandler, useState } from "react";

import TextArea from "components/TextArea";
import TextInput from "components/TextInput";

import styles from "./ContactSales.module.scss";

interface ContactSalesProps {
  onClose: () => void;
}

const ContactSales: React.FC<ContactSalesProps> = ({ onClose }) => {
  const { addToast } = useToast();
  const { sendEvent } = useAnalytics();
  const [phoneNumber, setPhoneNumber] = useState<string>();
  const [message, setMessage] = useState<string>();
  const [{ loading }, contactSales] = useContactSalesMutation();

  const handleClose = () => {
    sendEvent("Close");
    onClose?.();
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    sendEvent("Send Button - Click", {
      telephone: !!phoneNumber,
      message,
    });
    contactSales({ data: { phoneNumber, message } })
      .then(() => {
        addToast({
          message: `Request sent`,
          variant: "success",
        });
        onClose();
      })
      .catch(() => {
        addToast({
          message: "There was an error sending your callback request",
          variant: "error",
        });
      });
  };

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setPhoneNumber(event.target.value);
  };

  const handleTextAreaChange: ChangeEventHandler<HTMLTextAreaElement> = (
    event
  ) => {
    setMessage(event.target.value);
  };

  return (
    <div>
      <Modal.Header
        title="Talk to us"
        subtitle="Leave a message and our sales team will get in touch via email."
        onClose={handleClose}
      />

      <form onSubmit={handleSubmit} data-testid="contact-sales-modal">
        <Modal.Body variant="light">
          <div className={styles.contactContainer}>
            <TextArea
              label="Message"
              onChange={handleTextAreaChange}
              value={message}
              placeholder="Start typing..."
              data-testid="contact-message"
              id="message-input"
              inputName="Message"
              data-autofocus
            />
          </div>
          <div className={styles.contactContainer}>
            <TextInput
              label="Phone number"
              autoFocus
              type="text"
              onChange={handleInputChange}
              placeholder="Add your phone number if you'd prefer us to give you a call"
              data-testid="contact-phone"
              id="phone-input"
              inputName="Phone"
            />
          </div>
        </Modal.Body>
        <Modal.Footer
          variant="dark"
          buttons={[
            {
              label: "Cancel",
              onClick: handleClose,
              ["data-testid"]: "contact-cancel",
              variant: "secondary",
              color: "interface",
              type: "button",
            },
            {
              disabled: loading,
              isLoading: loading,
              label: "Send",
              type: "submit",
              ["data-testid"]: "contact-submit",
            },
          ]}
        />
      </form>
    </div>
  );
};

export default ContactSales;
